<template>
  <div id="app">
    <div class="container">
      <h1>Скрапер новостей</h1>
      
      <!-- Статус сервера и автоматизации -->
      <div class="status-bar">
        <div class="server-status" :class="{ 'server-online': serverAvailable, 'server-offline': !serverAvailable }">
          Статус сервера: {{ serverAvailable ? 'Онлайн' : 'Офлайн' }}
        </div>
        <div class="auto-status" :class="{ 'auto-enabled': autoParsingEnabled || autoPostingEnabled, 'auto-disabled': !autoParsingEnabled && !autoPostingEnabled }">
          Автоматизация: {{ (autoParsingEnabled || autoPostingEnabled) ? 'Включена' : 'Выключена' }}
        </div>
      </div>
      
      <!-- Форма для поиска новостей -->
      <div class="search-form">
        <div class="form-group">
          <label for="keywords">Ключевые слова (через запятую):</label>
          <input 
            type="text" 
            id="keywords" 
            v-model="keywords" 
            placeholder="Введите ключевые слова"
            @keyup.enter="searchNews"
          >
        </div>
        
        <div class="form-group">
          <label for="language">Язык:</label>
          <select id="language" v-model="language">
            <option value="ru">Русский</option>
            <option value="en">Английский</option>
            <option value="de">Немецкий</option>
            <option value="fr">Французский</option>
            <option value="es">Испанский</option>
          </select>
        </div>
        
        <div class="form-group">
          <label for="hoursAgo">Искать за последние (часов):</label>
          <input type="number" id="hoursAgo" v-model.number="hoursAgo" min="1" max="168">
        </div>
        
        <!-- API настройки -->
        <div class="api-settings">
          <h3>Настройки API</h3>
          
          <div class="form-group">
            <label for="newsApiKey">News API Key:</label>
            <input type="text" id="newsApiKey" v-model="newsApiKey" placeholder="Введите ваш News API Key">
          </div>
          
          <div class="form-group">
            <label for="currentsApiKey">Currents API Key:</label>
            <input type="text" id="currentsApiKey" v-model="currentsApiKey" placeholder="Введите ваш Currents API Key">
          </div>
          
          <div class="form-group">
            <label for="newsDataApiKey">NewsData.io API Key:</label>
            <input type="text" id="newsDataApiKey" v-model="newsDataApiKey" placeholder="Введите ваш NewsData.io API Key" disabled>
            <small>API ключ уже установлен</small>
          </div>
          
          <div class="api-checkboxes">
            <div class="form-check">
              <input type="checkbox" id="useNewsApi" v-model="useNewsApi">
              <label for="useNewsApi">Использовать News API</label>
            </div>
            
            <div class="form-check">
              <input type="checkbox" id="useCurrentsApi" v-model="useCurrentsApi">
              <label for="useCurrentsApi">Использовать Currents API</label>
            </div>
            
            <div class="form-check">
              <input type="checkbox" id="useNewsDataApi" v-model="useNewsDataApi">
              <label for="useNewsDataApi">Использовать NewsData.io API</label>
            </div>
          </div>
        </div>
        
        <!-- Настройки Telegram -->
        <div class="telegram-settings">
          <h3>Настройки Telegram</h3>
          
          <div class="form-group">
            <label for="telegramBotToken">Telegram Bot Token:</label>
            <input type="text" id="telegramBotToken" v-model="telegramBotToken" placeholder="Введите ваш Telegram Bot Token">
          </div>
          
          <div class="form-group">
            <label for="telegramChannelId">Telegram Channel ID:</label>
            <input type="text" id="telegramChannelId" v-model="telegramChannelId" placeholder="Введите ID канала или чата">
          </div>
        </div>
        
        <!-- Настройки автоматизации -->
        <div class="automation-settings">
          <h3>Настройки автоматизации</h3>
          
          <div class="form-check">
            <input type="checkbox" id="autoParsingEnabled" v-model="autoParsingEnabled">
            <label for="autoParsingEnabled">Автоматический парсинг новостей (каждые 60 минут)</label>
          </div>
          
          <div class="form-check">
            <input type="checkbox" id="autoPostingEnabled" v-model="autoPostingEnabled">
            <label for="autoPostingEnabled">Автоматический постинг в Telegram (каждые 15 минут)</label>
          </div>
          
          <div class="form-group" v-if="autoPostingEnabled">
            <label for="postsPerInterval">Количество постов за интервал:</label>
            <input type="number" id="postsPerInterval" v-model.number="postsPerInterval" min="1" max="10">
          </div>
          
          <div class="automation-status" v-if="autoParsingEnabled || autoPostingEnabled">
            <div v-if="autoParsingEnabled">
              Следующий парсинг через: {{ formatTimeRemaining(nextParsingTime) }}
            </div>
            <div v-if="autoPostingEnabled">
              Следующий постинг через: {{ formatTimeRemaining(nextPostingTime) }}
            </div>
          </div>
        </div>
        
        <div class="form-actions">
          <button @click="searchNews" :disabled="isLoading">
            {{ isLoading ? 'Загрузка...' : 'Найти новости' }}
          </button>
        </div>
      </div>
      
      <!-- Результаты поиска -->
      <div class="search-results" v-if="news.length > 0 || isLoading">
        <h2>Результаты поиска</h2>
        
        <div v-if="isLoading" class="loading">
          Загрузка новостей...
        </div>
        
        <div v-else>
          <div class="results-header">
            <p>Найдено {{ news.length }} новостей</p>
            <div class="queue-info" v-if="postingQueue.length > 0">
              <span>В очереди на публикацию: {{ postingQueue.length }}</span>
              <button @click="processPostingQueue(true)" class="small-button">
                Опубликовать сейчас
              </button>
            </div>
          </div>
          
          <div class="news-list">
            <div v-for="(item, index) in news" :key="index" class="news-item" :class="{ 'news-posted': item.isPosted || isAlreadyPosted(item) }">
              <div class="news-source">{{ item.source }} ({{ item.apiSource }})</div>
              <h3 class="news-title">
                <a :href="item.url" target="_blank">{{ item.title }}</a>
              </h3>
              
              <div class="news-meta">
                <span class="news-date">{{ formatDate(item.publishedAt) }}</span>
                <span v-if="item.similarity" class="news-similarity">
                  Схожесть: {{ Math.round(item.similarity * 100) }}%
                </span>
              </div>
              
              <div class="news-image" v-if="item.image">
                <img :src="item.image" :alt="item.title">
              </div>
              
              <div class="news-description">{{ cleanText(item.description) }}</div>
              
              <div class="news-actions">
                <button @click="generateContent(item)" :disabled="isGenerating[index] || isAlreadyPosted(item)">
                  {{ isGenerating[index] ? 'Генерация...' : 'Сгенерировать контент' }}
                </button>
                <button @click="postToTelegram(item, index)" :disabled="isPosting[index] || isAlreadyPosted(item)">
                  {{ isPosting[index] ? 'Отправка...' : 'Опубликовать в Telegram' }}
                </button>
                <button @click="addToQueue(item)" :disabled="isInQueue(item) || isAlreadyPosted(item)" class="queue-button">
                  {{ isInQueue(item) ? 'В очереди' : 'Добавить в очередь' }}
                </button>
              </div>
              
              <div class="generated-content" v-if="item.generatedContent">
                <h4>Сгенерированный контент:</h4>
                <div v-html="formatContent(cleanText(item.generatedContent))"></div>
                
                <div class="hashtags" v-if="item.hashtags && item.hashtags.length > 0">
                  <h4>Хэштеги:</h4>
                  <div class="hashtag-list">
                    <span v-for="(tag, tagIndex) in item.hashtags" :key="tagIndex" class="hashtag">
                      #{{ tag }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div v-else-if="hasSearched && !isLoading" class="no-results">
        <p>Новости не найдены. Попробуйте изменить параметры поиска.</p>
      </div>
    </div>
  </div>
  </template>
  
  <script>
  import NewsService from './services/NewsService';
  import AIService from './services/AIService';
  import TelegramService from './services/TelegramService';
  import HashtagService from './services/HashtagService';
  import TextProcessingService from './services/TextProcessingService';
  
  export default {
  name: 'App',
  data() {
    return {
      // Параметры поиска
      keywords: 'новости',
      language: 'ru',
      hoursAgo: 24,
      
      // API ключи
      newsApiKey: '',
      currentsApiKey: '',
      newsDataApiKey: 'pub_74322d7c525a8100aa962a0895c98604c24a5',
      
      // Настройки API
      useNewsApi: true,
      useCurrentsApi: true,
      useNewsDataApi: true,
      
      // Данные
      news: [],
      hasSearched: false,
      serverAvailable: false,
      
      // Состояния
      isLoading: false,
      isGenerating: {},
      isPosting: {},
      
      // Настройки Telegram
      telegramBotToken: '',
      telegramChannelId: '',
      
      // Настройки автоматизации
      autoParsingEnabled: false,
      autoPostingEnabled: false,
      parsingIntervalId: null,
      postingIntervalId: null,
      nextParsingTime: null,
      nextPostingTime: null,
      postsPerInterval: 1,
      
      // Очередь публикаций
      postingQueue: [],
      postedItems: [], // История опубликованных новостей
      
      // Таймеры для обратного отсчета
      countdownIntervalId: null
    };
  },
  async mounted() {
    // Инициализация сервисов
    await this.initServices();
    
    // Загрузка сохраненных настроек
    this.loadSettings();
    
    // Автоматический поиск при загрузке
    this.searchNews();
    
    // Запуск таймера обратного отсчета
    this.startCountdownTimer();
    
    // Если автопостинг включен, запускаем первую публикацию через 5 секунд после загрузки
    if (this.autoPostingEnabled) {
      setTimeout(() => {
        this.processPostingQueue();
      }, 5000);
    }
  },
  beforeDestroy() {
    // Очистка всех интервалов при уничтожении компонента
    this.clearAllIntervals();
  },
  methods: {
    async initServices() {
      try {
        // Инициализация NewsService
        await NewsService.init(
          this.newsApiKey, 
          this.currentsApiKey,
          false,
          this.newsDataApiKey
        );
        
        // Получаем статус сервера
        this.serverAvailable = NewsService.serverAvailable;
        
        // Инициализация TelegramService
        TelegramService.init(this.telegramBotToken, this.telegramChannelId);
        
        console.log('Сервисы инициализированы');
      } catch (error) {
        console.error('Ошибка при инициализации сервисов:', error);
      }
    },
    
    // Загрузка сохраненных настроек
    loadSettings() {
      try {
        const savedSettings = localStorage.getItem('newsScraperSettings');
        if (savedSettings) {
          const settings = JSON.parse(savedSettings);
          
          // Загружаем настройки
          this.keywords = settings.keywords || this.keywords;
          this.language = settings.language || this.language;
          this.hoursAgo = settings.hoursAgo || this.hoursAgo;
          this.newsApiKey = settings.newsApiKey || this.newsApiKey;
          this.currentsApiKey = settings.currentsApiKey || this.currentsApiKey;
          this.telegramBotToken = settings.telegramBotToken || this.telegramBotToken;
          this.telegramChannelId = settings.telegramChannelId || this.telegramChannelId;
          this.useNewsApi = settings.useNewsApi !== undefined ? settings.useNewsApi : this.useNewsApi;
          this.useCurrentsApi = settings.useCurrentsApi !== undefined ? settings.useCurrentsApi : this.useCurrentsApi;
          this.useNewsDataApi = settings.useNewsDataApi !== undefined ? settings.useNewsDataApi : this.useNewsDataApi;
          this.autoParsingEnabled = settings.autoParsingEnabled || false;
          this.autoPostingEnabled = settings.autoPostingEnabled || false;
          this.postsPerInterval = settings.postsPerInterval || 1;
          
          // Загружаем историю публикаций
          if (settings.postedItems) {
            this.postedItems = settings.postedItems;
          }
          
          console.log('Настройки загружены из localStorage');
        }
        
        // Запускаем автоматизацию, если она была включена
        if (this.autoParsingEnabled) {
          this.startAutoParsing();
        }
        
        if (this.autoPostingEnabled) {
          this.startAutoPosting();
        }
      } catch (error) {
        console.error('Ошибка при загрузке настроек:', error);
      }
    },
    
    // Сохранение настроек
    saveSettings() {
      try {
        const settings = {
          keywords: this.keywords,
          language: this.language,
          hoursAgo: this.hoursAgo,
          newsApiKey: this.newsApiKey,
          currentsApiKey: this.currentsApiKey,
          telegramBotToken: this.telegramBotToken,
          telegramChannelId: this.telegramChannelId,
          useNewsApi: this.useNewsApi,
          useCurrentsApi: this.useCurrentsApi,
          useNewsDataApi: this.useNewsDataApi,
          autoParsingEnabled: this.autoParsingEnabled,
          autoPostingEnabled: this.autoPostingEnabled,
          postsPerInterval: this.postsPerInterval,
          postedItems: this.postedItems.slice(0, 100) // Сохраняем только последние 100 публикаций
        };
        
        localStorage.setItem('newsScraperSettings', JSON.stringify(settings));
        console.log('Настройки сохранены в localStorage');
      } catch (error) {
        console.error('Ошибка при сохранении настроек:', error);
      }
    },
    
    // Поиск новостей
    async searchNews() {
      if (!this.keywords.trim()) {
        alert('Пожалуйста, введите ключевые слова для поиска');
        return;
      }
      
      this.isLoading = true;
      this.hasSearched = true;
      
      try {
        // Разбиваем ключевые слова по запятой
        const keywordArray = this.keywords.split(',').map(k => k.trim()).filter(k => k);
        
        // Получаем новости
        const results = await NewsService.getNews(
          keywordArray, 
          this.language, 
          this.hoursAgo,
          {
            useNewsApi: this.useNewsApi,
            useCurrentsApi: this.useCurrentsApi,
            useNewsDataApi: this.useNewsDataApi
          }
        );
        
        // Удаляем похожие новости
        const uniqueResults = this.removeSimilarNews(results);
        
        this.news = uniqueResults;
        console.log(`Получено ${this.news.length} уникальных новостей`);
        
        // Сохраняем настройки после успешного поиска
        this.saveSettings();
      } catch (error) {
        console.error('Ошибка при поиске новостей:', error);
        alert(`Ошибка при поиске новостей: ${error.message}`);
      } finally {
        this.isLoading = false;
      }
    },
    
    // Удаление похожих новостей
    removeSimilarNews(newsItems) {
      if (!newsItems || newsItems.length === 0) return [];
      
      console.log('Удаление похожих новостей...');
      
      // Сортируем новости по дате (от новых к старым)
      const sortedNews = [...newsItems].sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
      
      // Массив для хранения уникальных новостей
      const uniqueNews = [];
      
      // Порог схожести (в процентах)
      const similarityThreshold = 0.7;
      
      // Проверяем каждую новость
      for (const item of sortedNews) {
        // Очищаем текст от нежелательных фраз
        item.description = this.cleanText(item.description);
        
        // Флаг, указывающий, является ли новость похожей на уже добавленные
        let isSimilar = false;
        
        // Проверяем схожесть с уже добавленными новостями
        for (const uniqueItem of uniqueNews) {
          const similarity = this.calculateSimilarity(item, uniqueItem);
          
          // Если схожесть выше порога, помечаем как похожую
          if (similarity > similarityThreshold) {
            isSimilar = true;
            item.similarity = similarity;
            break;
          }
        }
        
        // Если новость не похожа на уже добавленные, добавляем ее
        if (!isSimilar) {
          uniqueNews.push(item);
        }
      }
      
      console.log(`Удалено ${sortedNews.length - uniqueNews.length} похожих новостей`);
      return uniqueNews;
    },
    
    // Расчет схожести двух новостей
    calculateSimilarity(item1, item2) {
      // Функция для получения набора слов из текста
      const getWords = (text) => {
        if (!text) return new Set();
        return new Set(
          text.toLowerCase()
            .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '')
            .split(/\s+/)
            .filter(word => word.length > 3)
        );
      };
      
      // Получаем наборы слов из заголовков и описаний
      const title1Words = getWords(item1.title);
      const title2Words = getWords(item2.title);
      const desc1Words = getWords(item1.description);
      const desc2Words = getWords(item2.description);
      
      // Объединяем слова из заголовка и описания
      const words1 = new Set([...title1Words, ...desc1Words]);
      const words2 = new Set([...title2Words, ...desc2Words]);
      
      // Если наборы пустые, возвращаем 0
      if (words1.size === 0 || words2.size === 0) return 0;
      
      // Находим пересечение наборов
      const intersection = new Set();
      for (const word of words1) {
        if (words2.has(word)) {
          intersection.add(word);
        }
      }
      
      // Вычисляем коэффициент Жаккара
      const similarity = intersection.size / (words1.size + words2.size - intersection.size);
      
      return similarity;
    },
    
    // Очистка текста от нежелательных фраз
    cleanText(text) {
      if (!text) return '';
      
      // Список фраз для удаления
      const phrasesToRemove = [
        'ONLY AVAILABLE IN PAID PLANS',
        'Subscribe to see more',
        'This content is available only to subscribers',
        'Read more at',
        'Read the full article',
        'Click here to read more',
        'Sign up for our newsletter',
        'For more information, visit',
        'Подробнее на сайте',
        'Читать далее',
        'Подписывайтесь на наш канал',
        'Полный текст статьи доступен по подписке'
      ];
      
      let cleanedText = text;
      
      // Удаляем каждую фразу
      for (const phrase of phrasesToRemove) {
        cleanedText = cleanedText.replace(new RegExp(phrase, 'gi'), '');
      }
      
      // Удаляем URL
      cleanedText = cleanedText.replace(/https?:\/\/[^\s]+/g, '');
      
      // Удаляем лишние пробелы
      cleanedText = cleanedText.replace(/\s+/g, ' ').trim();
      
      return cleanedText;
    },
    
    // Генерация контента
    async generateContent(newsItem) {
      const index = this.news.indexOf(newsItem);
      
      if (index === -1) return;
      
      // Устанавливаем флаг генерации для этой новости
      this.$set(this.isGenerating, index, true);
      
      try {
        // Очищаем текст от нежелательных фраз
        const cleanTitle = this.cleanText(newsItem.title);
        const cleanDescription = this.cleanText(newsItem.description);
        
        // Генерируем контент с помощью AI
        const content = await AIService.generateContent(cleanTitle, cleanDescription);
        
        // Генерируем хэштеги
        const hashtags = await HashtagService.generateHashtags(cleanTitle, content);
        
        // Обновляем новость с сгенерированным контентом
        this.$set(newsItem, 'generatedContent', content);
        this.$set(newsItem, 'hashtags', hashtags);
      } catch (error) {
        console.error('Ошибка при генерации контента:', error);
        alert(`Ошибка при генерации контента: ${error.message}`);
      } finally {
        // Сбрасываем флаг генерации
        this.$set(this.isGenerating, index, false);
      }
    },
    
    // Публикация в Telegram
    async postToTelegram(newsItem, index) {
      if (!newsItem.generatedContent) {
        await this.generateContent(newsItem);
      }
      
      // Устанавливаем флаг отправки для этой новости
      this.$set(this.isPosting, index, true);
      
      try {
        // Проверяем, установлены ли настройки Telegram
        if (!this.telegramBotToken || !this.telegramChannelId) {
          throw new Error('Пожалуйста, установите Telegram Bot Token и Channel ID в настройках');
        }
        
        // Обновляем настройки Telegram
        TelegramService.init(this.telegramBotToken, this.telegramChannelId);
        
        // Формируем сообщение для Telegram (без ссылки на источник)
        let message = `<b>${this.cleanText(newsItem.title)}</b>\n\n`;
        message += this.cleanText(newsItem.generatedContent);
        
        // Добавляем хэштеги, если они есть
        if (newsItem.hashtags && newsItem.hashtags.length > 0) {
          message += '\n\n';
          message += newsItem.hashtags.map(tag => `#${tag}`).join(' ');
        }
        
        // Отправляем сообщение в Telegram
        const result = await TelegramService.sendMessage(message, newsItem.image);
        
        if (result.success) {
          // Добавляем новость в историю публикаций
          this.addToPostedItems(newsItem);
          
          // Отмечаем новость как опубликованную
          this.$set(newsItem, 'isPosted', true);
          
          // Убираем alert с уведомлением
          console.log('Сообщение успешно отправлено в Telegram!');
        } else {
          throw new Error(result.error || 'Неизвестная ошибка');
        }
      } catch (error) {
        console.error('Ошибка при отправке в Telegram:', error);
        alert(`Ошибка при отправке в Telegram: ${error.message}`);
      } finally {
        // Сбрасываем флаг отправки
        this.$set(this.isPosting, index, false);
      }
    },
    
    // Добавление новости в очередь публикаций
    addToQueue(newsItem) {
      // Проверяем, есть ли уже эта новость в очереди
      if (this.isInQueue(newsItem)) return;
      
      // Если контент еще не сгенерирован, генерируем его
      if (!newsItem.generatedContent) {
        this.generateContent(newsItem).then(() => {
          // Добавляем в очередь после генерации контента
          this.postingQueue.push({
            ...newsItem,
            queuedAt: new Date()
          });
          console.log(`Новость "${newsItem.title}" добавлена в очередь публикаций`);
        });
      } else {
        // Добавляем в очередь
        this.postingQueue.push({
          ...newsItem,
          queuedAt: new Date()
        });
        console.log(`Новость "${newsItem.title}" добавлена в очередь публикаций`);
      }
    },
    
    // Проверка, находится ли новость в очереди
    isInQueue(newsItem) {
      return this.postingQueue.some(item => item.url === newsItem.url);
    },
    
    // Обработка очереди публикаций
    async processPostingQueue(force = false) {
      // Если это не принудительная публикация и автопостинг выключен, выходим
      if (!force && !this.autoPostingEnabled) return;
      
      console.log('Запуск автоматического постинга...');
      
      // Если очередь пуста, но есть новости, автоматически выбираем новости для публикации
      if (this.postingQueue.length === 0 && this.news.length > 0) {
        // Выбираем новости, которые еще не были опубликованы
        const unpublishedNews = this.news.filter(item => !this.isAlreadyPosted(item));
        
        if (unpublishedNews.length > 0) {
          // Берем первые postsPerInterval новостей или все доступные, если их меньше
          const newsToQueue = unpublishedNews.slice(0, this.postsPerInterval);
          
          // Генерируем контент для каждой новости, если его еще нет
          for (const item of newsToQueue) {
            if (!item.generatedContent) {
              await this.generateContent(item);
            }
            
            // Добавляем в очередь
            this.postingQueue.push({
              ...item,
              queuedAt: new Date()
            });
          }
          
          console.log(`Автоматически добавлено ${newsToQueue.length} новостей в очередь публикаций`);
        } else {
          console.log('Нет неопубликованных новостей для добавления в очередь');
          return;
        }
      }
      
      if (this.postingQueue.length === 0) {
        console.log('Очередь публикаций пуста');
        return;
      }
      
      // Количество новостей для публикации
      const count = Math.min(this.postsPerInterval, this.postingQueue.length);
      
      // Публикуем указанное количество новостей
      for (let i = 0; i < count; i++) {
        const newsItem = this.postingQueue.shift(); // Извлекаем первую новость из очереди
        
        try {
          // Проверяем, установлены ли настройки Telegram
          if (!this.telegramBotToken || !this.telegramChannelId) {
            throw new Error('Не установлены настройки Telegram');
          }
          
          // Обновляем настройки Telegram
          TelegramService.init(this.telegramBotToken, this.telegramChannelId);
          
          // Формируем сообщение для Telegram (без ссылки на источник)
          let message = `<b>${this.cleanText(newsItem.title)}</b>\n\n`;
          message += this.cleanText(newsItem.generatedContent);
          
          // Добавляем хэштеги, если они есть
          if (newsItem.hashtags && newsItem.hashtags.length > 0) {
            message += '\n\n';
            message += newsItem.hashtags.map(tag => `#${tag}`).join(' ');
          }
          
          // Отправляем сообщение в Telegram
          const result = await TelegramService.sendMessage(message, newsItem.image);
          
          if (result.success) {
            console.log(`Новость "${newsItem.title}" успешно опубликована в Telegram`);
            
            // Добавляем новость в историю публикаций
            this.addToPostedItems(newsItem);
            
            // Отмечаем новость как опубликованную в основном списке
            const originalIndex = this.news.findIndex(item => item.url === newsItem.url);
            if (originalIndex !== -1) {
              this.$set(this.news[originalIndex], 'isPosted', true);
            }
          } else {
            throw new Error(result.error || 'Неизвестная ошибка при отправке в Telegram');
          }
        } catch (error) {
          console.error(`Ошибка при публикации новости "${newsItem.title}":`, error);
          
          // Возвращаем новость в конец очереди, если это не ошибка настроек
          if (!error.message.includes('Не установлены настройки Telegram')) {
            this.postingQueue.push(newsItem);
          }
        }
        
        // Делаем паузу между публикациями, чтобы не перегружать API Telegram
        if (i < count - 1) {
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
      }
      
      // Обновляем время следующей публикации
      this.updateNextPostingTime();
      
      // Сохраняем настройки после публикации
      this.saveSettings();
    },
    
    // Добавление новости в историю публикаций
    addToPostedItems(newsItem) {
      // Добавляем только если новости еще нет в истории
      if (!this.postedItems.some(item => item.url === newsItem.url)) {
        this.postedItems.unshift({
          url: newsItem.url,
          title: newsItem.title,
          publishedAt: newsItem.publishedAt,
          postedAt: new Date()
        });
        
        // Ограничиваем размер истории
        if (this.postedItems.length > 200) {
          this.postedItems = this.postedItems.slice(0, 200);
        }
        
        // Сохраняем историю
        this.saveSettings();
      }
    },
    
    // Проверка, была ли новость уже опубликована
    isAlreadyPosted(newsItem) {
      // Проверяем по URL в истории публикаций
      return this.postedItems.some(item => item.url === newsItem.url) || 
             // Также проверяем флаг isPosted в самой новости
             newsItem.isPosted === true;
    },
    
    // Запуск автоматического парсинга
    startAutoParsing() {
      // Очищаем предыдущий интервал, если он был
      if (this.parsingIntervalId) {
        clearInterval(this.parsingIntervalId);
      }
      
      // Устанавливаем время следующего парсинга
      this.updateNextParsingTime();
      
      // Запускаем интервал (60 минут)
      this.parsingIntervalId = setInterval(() => {
        console.log('Запуск автоматического парсинга новостей...');
        this.searchNews();
        this.updateNextParsingTime();
      }, 60 * 60 * 1000);
      
      console.log('Автоматический парсинг новостей включен');
    },
    
    // Остановка автоматического парсинга
    stopAutoParsing() {
      if (this.parsingIntervalId) {
        clearInterval(this.parsingIntervalId);
        this.parsingIntervalId = null;
        this.nextParsingTime = null;
        console.log('Автоматический парсинг новостей выключен');
      }
    },
    
    // Запуск автоматического постинга
    startAutoPosting() {
      // Очищаем предыдущий интервал, если он был
      if (this.postingIntervalId) {
        clearInterval(this.postingIntervalId);
      }
      
      // Устанавливаем время следующего постинга
      this.updateNextPostingTime();
      
      // Запускаем интервал (15 минут)
      this.postingIntervalId = setInterval(() => {
        console.log('Запуск автоматического постинга...');
        this.processPostingQueue();
        this.updateNextPostingTime();
      }, 15 * 60 * 1000);
      
      console.log('Автоматический постинг включен');
    },
    
    // Остановка автоматического постинга
    stopAutoPosting() {
      if (this.postingIntervalId) {
        clearInterval(this.postingIntervalId);
        this.postingIntervalId = null;
        this.nextPostingTime = null;
        console.log('Автоматический постинг выключен');
      }
    },
    
    // Обновление времени следующего парсинга
    updateNextParsingTime() {
      const now = new Date();
      this.nextParsingTime = new Date(now.getTime() + 60 * 60 * 1000);
    },
    
    // Обновление времени следующего постинга
    updateNextPostingTime() {
      const now = new Date();
      this.nextPostingTime = new Date(now.getTime() + 15 * 60 * 1000);
    },
    
    // Запуск таймера обратного отсчета
    startCountdownTimer() {
      // Очищаем предыдущий интервал, если он был
      if (this.countdownIntervalId) {
        clearInterval(this.countdownIntervalId);
      }
      
      // Запускаем интервал (1 секунда)
      this.countdownIntervalId = setInterval(() => {
        // Обновляем компонент для отображения актуального времени
        this.$forceUpdate();
      }, 1000);
    },
    
    // Форматирование оставшегося времени
    formatTimeRemaining(targetTime) {
      if (!targetTime) return '--:--:--';
      
      const now = new Date();
      const diff = targetTime - now;
      
      if (diff <= 0) return '00:00:00';
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    },
    
    // Очистка всех интервалов
    clearAllIntervals() {
      if (this.parsingIntervalId) {
        clearInterval(this.parsingIntervalId);
      }
      
      if (this.postingIntervalId) {
        clearInterval(this.postingIntervalId);
      }
      
      if (this.countdownIntervalId) {
        clearInterval(this.countdownIntervalId);
      }
    },
    
    // Форматирование даты
    formatDate(dateString) {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      
      // Проверяем, что дата валидна
      if (isNaN(date.getTime())) return dateString;
      
      return date.toLocaleString();
    },
    
    // Форматирование контента
    formatContent(content) {
      if (!content) return '';
      
      // Заменяем переносы строк на <br>
      return content.replace(/\n/g, '<br>');
    }
  },
  watch: {
    // Следим за изменением настроек автоматизации
    autoParsingEnabled(newValue) {
      if (newValue) {
        this.startAutoParsing();
      } else {
        this.stopAutoParsing();
      }
      this.saveSettings();
    },
    
    autoPostingEnabled(newValue) {
      if (newValue) {
        this.startAutoPosting();
      } else {
        this.stopAutoPosting();
      }
      this.saveSettings();
    },
    
    // Следим за изменением API ключей
    newsApiKey() {
      this.initServices();
      this.saveSettings();
    },
    
    currentsApiKey() {
      this.initServices();
      this.saveSettings();
    },
    
    // Следим за изменением настроек Telegram
    telegramBotToken() {
      TelegramService.init(this.telegramBotToken, this.telegramChannelId);
      this.saveSettings();
    },
    
    telegramChannelId() {
      TelegramService.init(this.telegramBotToken, this.telegramChannelId);
      this.saveSettings();
    }
  }
  };
  </script>
  
  <style>
  #app {
  font-family: 'Roboto', 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  margin: 0;
  padding: 0;
  }
  
  .container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  }
  
  h1 {
  text-align: center;
  margin-bottom: 30px;
  }
  
  .status-bar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  }
  
  .server-status, .auto-status {
  padding: 10px;
  border-radius: 4px;
  font-weight: bold;
  flex: 1;
  text-align: center;
  margin: 0 5px;
  }
  
  .server-online {
  background-color: #d4edda;
  color: #155724;
  }
  
  .server-offline {
  background-color: #f8d7da;
  color: #721c24;
  }
  
  .auto-enabled {
  background-color: #cce5ff;
  color: #004085;
  }
  
  .auto-disabled {
  background-color: #e2e3e5;
  color: #383d41;
  }
  
  .search-form {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .form-group {
  margin-bottom: 15px;
  }
  
  .form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  }
  
  .form-group input,
  .form-group select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 16px;
  }
  
  .api-settings,
  .telegram-settings,
  .automation-settings {
  margin-top: 20px;
  padding: 15px;
  background-color: #e9ecef;
  border-radius: 4px;
  }
  
  .api-settings h3,
  .telegram-settings h3,
  .automation-settings h3 {
  margin-top: 0;
  margin-bottom: 15px;
  }
  
  .api-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 15px;
  }
  
  .form-check {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  }
  
  .form-check input {
  margin-right: 8px;
  width: auto;
  }
  
  .automation-status {
  margin-top: 15px;
  padding: 10px;
  background-color: #f1f1f1;
  border-radius: 4px;
  font-size: 14px;
  }
  
  .form-actions {
  margin-top: 20px;
  text-align: center;
  }
  
  button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s;
  }
  
  button:hover {
  background-color: #0069d9;
  }
  
  button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  }
  
  .small-button {
  padding: 5px 10px;
  font-size: 14px;
  }
  
  .queue-button {
  background-color: #6c757d;
  }
  
  .queue-button:hover {
  background-color: #5a6268;
  }
  
  .search-results {
  margin-top: 30px;
  }
  
  .results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  }
  
  .queue-info {
  display: flex;
  align-items: center;
  gap: 10px;
  }
  
  .loading {
  text-align: center;
  padding: 20px;
  font-style: italic;
  }
  
  .news-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-top: 20px;
  }
  
  .news-item {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 15px;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: transform 0.2s;
    position: relative; /* Добавлено для позиционирования метки "Опубликовано" */
  }
  
  .news-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
  
  .news-source {
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 5px;
  }
  
  .news-title {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 18px;
  }
  
  .news-title a {
  color: #007bff;
  text-decoration: none;
  }
  
  .news-title a:hover {
  text-decoration: underline;
  }
  
  .news-meta {
  margin-bottom: 10px;
  font-size: 14px;
  color: #6c757d;
  display: flex;
  justify-content: space-between;
  }
  
  .news-similarity {
  color: #dc3545;
  font-weight: bold;
  }
  
  .news-image {
  margin-bottom: 15px;
  text-align: center;
  }
  
  .news-image img {
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
  object-fit: cover;
  }
  
  .news-description {
  margin-bottom: 15px;
  font-size: 14px;
  line-height: 1.5;
  }
  
  .news-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
  }
  
  .generated-content {
  margin-top: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.6;
  }
  
  .hashtags {
  margin-top: 15px;
  }
  
  .hashtag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  }
  
  .hashtag {
  background-color: #e9ecef;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #495057;
  }
  
  .no-results {
  text-align: center;
  padding: 30px;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-top: 30px;
  }
  
  @media (max-width: 768px) {
  .news-list {
    grid-template-columns: 1fr;
  }
  
  .api-checkboxes {
    flex-direction: column;
    gap: 10px;
  }
  
  .status-bar {
    flex-direction: column;
    gap: 10px;
  }
  
  .server-status, .auto-status {
    margin: 5px 0;
  }
  
  .news-actions {
    flex-direction: column;
  }
  }
  
  .news-posted {
    border-left: 4px solid #28a745;
    opacity: 0.8;
  }
  
  .news-posted::after {
    content: "✓ Опубликовано";
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: #28a745;
    color: white;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
  }
  </style>
  
  